// AIHighlights.tsx
// Compact AI-powered section highlighting AI Agents and integration capabilities

import React from 'react';
import { motion } from 'framer-motion';
import { Zap, Shield, ArrowRight, Bot, Layers, Sparkles } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { Link } from 'react-router-dom';
import styles from './AIHighlights.module.css';

export const AIHighlights: React.FC = () => {
    const { language } = useLanguage();

    const content = {
        de: {
            tag: 'KI-INTEGRATION',
            title: 'KI-gestützte Transformation',
            titleAccent: '– Ihre Daten, Ihre Kontrolle',
            subtitle: 'Wir integrieren AI Agents direkt in Ihre Kernprozesse – von SAP bis Cloud. Maximale Automatisierung bei voller Datensouveränität.',
            cta: 'Strategic Assessment starten',
            cardNote: 'Teil des Strategic Enterprise Assessments',
            cards: [
                {
                    icon: Bot,
                    title: 'AI Agents',
                    desc: 'Intelligente Agenten automatisieren Dokumente, Logistik und Finanzen.',
                    color: '#8b5cf6' // Purple
                },
                {
                    icon: Layers,
                    title: 'Systemintegration',
                    desc: 'Nahtlose Einbindung in ERP, SAP und bestehende Workflows.',
                    color: '#3b82f6' // Blue
                },
                {
                    icon: Shield,
                    title: 'Datenhoheit',
                    desc: 'EU-konform: Ihre Daten bleiben in Ihrer Kontrolle.',
                    color: '#10b981' // Green
                },
                {
                    icon: Zap,
                    title: 'Schnelle Ergebnisse',
                    desc: 'Produktive KI-Use-Cases in 4-8 Wochen live.',
                    color: '#f59e0b' // Amber
                }
            ]
        },
        en: {
            tag: 'AI INTEGRATION',
            title: 'AI-powered Transformation',
            titleAccent: '– Your Data, Your Control',
            subtitle: 'We integrate AI Agents directly into your core processes – from SAP to Cloud. Maximum automation with full data sovereignty.',
            cta: 'Start Strategic Assessment',
            cardNote: 'Part of the Strategic Enterprise Assessment',
            cards: [
                {
                    icon: Bot,
                    title: 'AI Agents',
                    desc: 'Intelligent agents automate documents, logistics, and finance.',
                    color: '#8b5cf6' // Purple
                },
                {
                    icon: Layers,
                    title: 'System Integration',
                    desc: 'Seamless integration with ERP, SAP, and existing workflows.',
                    color: '#3b82f6' // Blue
                },
                {
                    icon: Shield,
                    title: 'Data Sovereignty',
                    desc: 'EU-compliant: Your data stays under your control.',
                    color: '#10b981' // Green
                },
                {
                    icon: Zap,
                    title: 'Fast Results',
                    desc: 'Productive AI use cases live in 4-8 weeks.',
                    color: '#f59e0b' // Amber
                }
            ]
        }
    };

    const t = content[language];

    return (
        <section className={styles.section}>
            <div className={styles.container}>
                {/* Header */}
                <motion.div
                    className={styles.header}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4 }}
                >
                    <span className={styles.tag}>
                        <Sparkles size={14} />
                        {t.tag}
                    </span>
                    <h2 className={styles.title}>
                        <span className={styles.titleAccent}>{t.title}</span>
                        {t.titleAccent}
                    </h2>
                    <p className={styles.subtitle}>{t.subtitle}</p>
                </motion.div>

                {/* Cards Grid */}
                <div className={styles.grid}>
                    {t.cards.map((card, index) => (
                        <motion.div
                            key={index}
                            className={styles.card}
                            style={{
                                borderLeftColor: card.color,
                                '--card-accent': card.color
                            } as React.CSSProperties}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.4, delay: index * 0.1 }}
                        >
                            <div
                                className={styles.cardIcon}
                                style={{
                                    background: `linear-gradient(135deg, ${card.color}20, ${card.color}08)`,
                                    color: card.color
                                }}
                            >
                                <card.icon size={24} />
                            </div>
                            <h3 className={styles.cardTitle}>{card.title}</h3>
                            <p className={styles.cardDesc}>{card.desc}</p>
                        </motion.div>
                    ))}
                </div>

                {/* CTA */}
                <motion.div
                    className={styles.ctaRow}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: 0.4 }}
                >
                    <Link to="/assessment" className={styles.ctaButton}>
                        <span>{t.cta}</span>
                        <ArrowRight size={18} />
                    </Link>
                </motion.div>
            </div>
        </section>
    );
};

export default AIHighlights;
